import { Link } from 'react-router-dom';
import {
  ArrowLeft,
  AlertTriangle,
  Upload,
  Waves,
  FileText,
  Trash2,
  Check,
  X,
  Mail,
  ArrowRight,
} from 'lucide-react';
import Layout from '../components/Layout';
import { Card } from '../components/ui';
import { linkButtonClass } from '../utils/linkButtonClass';
import { useDocumentMeta } from '../hooks/useDocumentMeta';

/**
 * 「かんたんな説明」ページ（/terms-simple）。
 *
 * 本体3文書（利用規約・プライバシーポリシー・特定商取引法に基づく表記）の要点を、
 * 条文を読み慣れていない利用者向けに平易な日本語で説明する補助資料。
 * **本ページ自体は法的文書ではない**（効力を持つのは本体3文書）。
 *
 * LegalPage に相乗りせず独立コンポーネントにした理由:
 * LegalPage は「見出し＋本文」の均一な条文レンダラであり、本ページが必要とする
 * ステップ図・プラン別の比較表・する/しない の対比・目次といった独自レイアウトを
 * 表現できない。LegalPage 側を汎用化すると条文3文書の表示に無関係な分岐が増え、
 * 監修済み文書のレンダリングを壊すリスクが上がるため分離した。
 *
 * 記載の根拠（重要・改変時は必ず守ること）:
 * - 事実は**すべて本体3文書のみ**を出典とする。LP・FAQ・仕様書・実装定数から書かない。
 *   条番号との対応は `docs/terms-simple-facts.md` を参照。
 * - 断定を強めない／弱めない。規約が条件付き（プラン限定・期間限定）で定めるものを
 *   無条件の約束として書かない（景品表示法上の有利誤認・不実告知を避けるため）。
 * - プラン名・金額・上限値は本体3文書に存在しないため本ページには書かず、/pricing へ誘導する。
 * - 外部事業者のうち本体3文書が名指ししているのは Stripe のみ。他は「クラウドインフラ事業者
 *   （委託先）」等の機能表記に留める（プライバシーポリシー第6条5項〜7項）。
 * - 監修表記は監修者指定の文言そのまま。監修対象が**本体3文書**であることが読み取れる
 *   文脈に置く。本ページ自体も 2026-08-15 に行政書士の確認を受け修正なしとなったが、
 *   ページ上の監修表記の対象は本体3文書のまま据え置く（監修表記の射程を勝手に広げない）。
 */

/** 本体3文書と共通の監修者表記（表記文言は監修者の指定どおり。改変・短縮禁止）。 */
const SUPERVISOR = '法的事項監修　くろさわ行政書士事務所　行政書士　黒澤英樹';

const LAST_UPDATED = '2026年9月29日';

/**
 * プライバシーポリシー第6条の条文名。原文どおり見出し内に全角スペースを含むため、
 * JSX へ直書きせず文字列定数にしている（eslint no-irregular-whitespace は
 * JSX テキストを検査するが、文字列リテラルは既定で対象外）。
 */
const PP_ARTICLE_SECRECY = '第6条（通信の秘密　個人情報等の保護）';

interface Section {
  id: string;
  title: string;
}

const sections: Section[] = [
  { id: 'audio-flow', title: 'あなたの音声はどこへ行き、いつ消えるのか' },
  { id: 'ai', title: 'AIに何をさせ、何をさせないのか' },
  { id: 'third-party', title: '外部に情報が渡る先' },
  { id: 'purchase', title: '時間・回数を買い足したとき' },
  { id: 'leaving', title: 'アカウントを消したいとき・解約したいとき' },
  { id: 'contact', title: '困ったときの連絡先' },
];

/** 音声の流れ（アップロード→処理→受け取り→削除）。出典は各 note に対応する条番号。 */
const flowSteps = [
  {
    icon: <Upload className="w-5 h-5 text-white" aria-hidden="true" />,
    title: '1. 音声をアップロードする',
    body: '文字起こしのために、otomochi が管理するクラウドサーバーへ音声をお預かりします。処理の都合上、外国にあるサーバーで処理されることがあります。その場合も、法令に従って安全管理の措置をとります。',
  },
  {
    icon: <Waves className="w-5 h-5 text-white" aria-hidden="true" />,
    title: '2. AIがテキストに変換する',
    body: '音声認識AIが音声を聞き取り、テキストに変換します。そのあと、あなたが登録した用語集をもとに、固有名詞や専門用語の表記ゆれを整えます。',
  },
  {
    icon: <FileText className="w-5 h-5 text-white" aria-hidden="true" />,
    title: '3. 書き起こし結果を受け取る',
    body: '結果画面で内容を確認し、ダウンロードできます。書き起こし結果の権利はあなたに帰属し、otomochi はその権利を主張しません。',
  },
  {
    icon: <Trash2 className="w-5 h-5 text-white" aria-hidden="true" />,
    title: '4. 自動的に削除される',
    body: '音声も書き起こし結果も、保持期限が来ると自動的に削除されます。期限はご利用のプランによって異なります（下の表をご覧ください）。',
  },
];

/** AIに「させること」。すべて利用規約 第6条1項の記載に対応。 */
const aiDoes = [
  {
    title: '音声を聞き取ってテキストにする',
    body: '音声認識AI「Faster-Whisper（OpenAI Whisperベース）」を使います。OpenAI が開発したモデルを高速化したもので、音声を聞き取り、忠実にテキストへ変換します。',
  },
  {
    title: '用語の表記ゆれを整える',
    body: 'LLM（文章を扱うAI）を使います。オープンソースとして公開された言語モデルを、otomochi が自ら保持・運用しています。あなたが登録した用語集にもとづいて、固有名詞・専門用語の表記ゆれを整えます。',
  },
];

/** AIに「させないこと」。すべて利用規約 第6条1項(2)・2項の記載に対応。 */
const aiDoesNot = [
  {
    title: '発言内容の創作・要約・改変',
    body: '用語補正AIが行うのは表記の補正のみです。新たなコンテンツの生成・創作や、発言内容の改変は行いません。',
  },
  {
    title: 'AIの学習・再学習への利用',
    body: 'あなたの音声データ・テキストデータを、AIの学習または再学習に利用しません。',
  },
  {
    title: '外部の生成AIサービスへの送信',
    body: 'otomochi が使うAIは、いずれも otomochi 自身が保持するモデルを otomochi の管理下で動かしています。OpenAI、Anthropic その他の第三者が提供する生成AIサービス（外部のAI API等）へ、あなたのデータを提供・送信することはありません。',
  },
  {
    title: '書き起こし・補正以外の目的での処理',
    body: 'AIによる処理は、書き起こしと補正の目的の範囲内でのみ行います。',
  },
];

/** 外部に情報が渡る先。実名を出せるのは本体3文書が名指ししている Stripe のみ。 */
const thirdParties = [
  {
    name: 'Stripe（決済代行サービス）',
    data: 'クレジットカード情報等の決済手段に関する情報',
    note: 'カード情報は Stripe が取得・管理します。otomochi があなたのカード情報を直接受け取ったり保存したりすることはありません。',
  },
  {
    name: '連携ログインの提供元（Google、Discord 等）',
    data: '連携サービスのアカウント情報',
    note: '外部サービスのアカウントでログインする機能を使った場合に、その連携サービスからアカウント情報が提供されます。',
  },
  {
    name: 'クラウドインフラ事業者',
    data: '音声データを含む、処理に必要な情報',
    note: '音声の処理とサービスの運営のために利用しています。個人情報の取扱いの委託先という位置づけで、otomochi が法令に従って必要かつ適切な監督を行います。外国にあるサーバーで処理される場合があります。',
  },
];

export default function TermsSimplePage() {
  useDocumentMeta(
    'かんたんな説明 | otomochi',
    'otomochi の利用規約・プライバシーポリシーの要点を、平易な言葉で説明した補助資料です。音声データの保持と削除、AIの使い方、外部に情報が渡る先、買い足した時間・回数の扱い、退会と解約の扱いをまとめています。'
  );

  return (
    <Layout showPromo>
      <header className="bg-white shadow-sm">
        <div className="max-w-3xl mx-auto px-4 py-4">
          <Link
            to="/"
            className="inline-flex items-center text-primary hover:text-primary-hover transition-colors"
          >
            <ArrowLeft className="w-5 h-5 mr-2" aria-hidden="true" />
            トップに戻る
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-12">
        <div className="text-center mb-8">
          <h1 className="text-3xl sm:text-4xl font-bold text-text-heading mb-3">かんたんな説明</h1>
          <p className="text-text-sub leading-relaxed">
            利用規約とプライバシーポリシーの要点を、やさしい言葉でまとめました。
          </p>
        </div>

        {/* 冒頭の但し書き。本体3文書が優先することを最初に明示する。 */}
        <div className="mb-10 rounded-panel border border-warning/40 bg-warning-bg px-5 py-5">
          <div className="flex items-start gap-3">
            <AlertTriangle
              className="w-6 h-6 text-warning-fg flex-shrink-0 mt-0.5"
              aria-hidden="true"
            />
            <div>
              <h2 className="font-bold text-text-heading mb-2">はじめにお読みください</h2>
              <ul className="text-sm text-text leading-relaxed space-y-1.5 list-disc list-outside pl-5">
                <li>
                  このページは、規約の要点を平易な言葉で説明した<strong>補助資料</strong>です。
                </li>
                <li>
                  法的な効力を持つのは、
                  <strong>利用規約・プライバシーポリシー・特定商取引法に基づく表記</strong>
                  の本文です。
                </li>
                <li>
                  このページと本文の記載が食い違う場合は、<strong>本文の記載が優先されます</strong>。
                </li>
              </ul>
              <div className="mt-4 flex flex-wrap gap-2">
                <Link to="/terms" className={linkButtonClass('secondary', '', 'sm')}>
                  利用規約
                </Link>
                <Link to="/privacy" className={linkButtonClass('secondary', '', 'sm')}>
                  プライバシーポリシー
                </Link>
                <Link to="/commerce" className={linkButtonClass('secondary', '', 'sm')}>
                  特定商取引法に基づく表記
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* 目次 */}
        <nav aria-label="目次" className="mb-12">
          <Card padding="sm">
            <p className="text-sm font-bold text-text-heading mb-2">このページの内容</p>
            <ol className="text-sm space-y-1.5 list-decimal list-outside pl-5 marker:text-text-sub">
              {sections.map((s) => (
                <li key={s.id}>
                  <a
                    href={`#${s.id}`}
                    className="text-primary hover:text-primary-hover underline underline-offset-2"
                  >
                    {s.title}
                  </a>
                </li>
              ))}
            </ol>
          </Card>
        </nav>

        {/* 1. 音声の流れ */}
        <section aria-labelledby="audio-flow" className="mb-16">
          <h2 id="audio-flow" className="text-2xl font-bold text-text-heading mb-3 scroll-mt-6">
            あなたの音声はどこへ行き、いつ消えるのか
          </h2>
          <p className="text-text-sub leading-relaxed mb-6">
            アップロードした音声がたどる道すじと、消えるタイミングです。
          </p>

          <ol className="space-y-4 mb-8">
            {flowSteps.map((step) => (
              <li key={step.title}>
                <Card className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-2xl bg-primary flex items-center justify-center flex-shrink-0">
                    {step.icon}
                  </div>
                  <div>
                    <h3 className="text-base font-bold text-text-heading mb-1">{step.title}</h3>
                    <p className="text-sm text-text leading-relaxed">{step.body}</p>
                  </div>
                </Card>
              </li>
            ))}
          </ol>

          <h3 className="text-lg font-bold text-text-heading mb-2">
            いつ消えるのか（プランによって異なります）
          </h3>
          <p className="text-sm text-text-sub leading-relaxed mb-4">
            「音声」と「書き起こし結果（テキスト）」は別のものとして扱われ、それぞれ保持期限が決まっています。
          </p>
          {/* 幅の狭い端末では表だけを横スクロールさせる（ページ全体は横に伸ばさない） */}
          <div className="overflow-x-auto rounded-panel shadow-card bg-card mb-4">
            <table className="w-full min-w-[560px] border-collapse text-left">
              <caption className="sr-only">
                プラン別の、音声データと書き起こし結果の保持期限
              </caption>
              <thead>
                <tr className="border-b border-text-sub/15">
                  <th scope="col" className="px-5 py-4 text-sm font-bold text-text-heading">
                    データの種類
                  </th>
                  <th scope="col" className="px-4 py-4 text-sm font-bold text-text-heading">
                    無制限プラン以外
                  </th>
                  <th scope="col" className="px-4 py-4 text-sm font-bold text-text-heading">
                    無制限プラン
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-text-sub/10">
                  <th scope="row" className="px-5 py-4 text-sm font-medium text-text align-top">
                    音声データ
                  </th>
                  <td className="px-4 py-4 text-sm text-text align-top">
                    書き起こし結果をお渡ししたあと、自動的に削除されます。クラウド上には保存しません。
                  </td>
                  <td className="px-4 py-4 text-sm text-text align-top">
                    結果画面で音声を聞き直せるようにするため、<strong>完了から7日間</strong>
                    サーバー上に保持します。アクセスできるのはご本人だけです。期限が来ると、書き起こし結果とともに自動的に削除されます。
                  </td>
                </tr>
                <tr>
                  <th scope="row" className="px-5 py-4 text-sm font-medium text-text align-top">
                    書き起こし結果（テキスト）
                  </th>
                  <td className="px-4 py-4 text-sm text-text align-top">
                    <strong>完了から8時間</strong>で自動的に削除されます。
                  </td>
                  <td className="px-4 py-4 text-sm text-text align-top">
                    <strong>完了から7日間</strong>で自動的に削除されます。
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <p className="text-sm text-text-sub leading-relaxed mb-6">
            必要な書き起こし結果は、期限が来る前にダウンロードしてください。プランごとの違いは
            <Link
              to="/pricing"
              className="mx-1 text-primary hover:text-primary-hover underline underline-offset-2"
            >
              料金プラン
            </Link>
            のページでご確認いただけます。
          </p>

          <Card className="space-y-3">
            <h3 className="text-base font-bold text-text-heading">あわせて知っておいてください</h3>
            <p className="text-sm text-text leading-relaxed">
              削除の対象になるのは、音声データと書き起こし結果です。
              <strong>ご利用の頻度や利用時間といった利用状況の情報は、上記の対象には含まれません。</strong>
            </p>
            <p className="text-sm text-text leading-relaxed">
              音声には、声紋などから特定の個人を識別できる情報が含まれる場合があります。また、セッション中の会話には、健康状態や思想信条といった配慮が必要な情報が含まれる可能性があります。この点をご理解のうえでご利用ください。
            </p>
            <p className="text-sm text-text leading-relaxed">
              アップロードする音声については、必要な権利をお持ちであること、録音に参加した方から同意を得ていることが、ご利用の前提条件になります。
            </p>
          </Card>
        </section>

        {/* 2. AI */}
        <section aria-labelledby="ai" className="mb-16">
          <h2 id="ai" className="text-2xl font-bold text-text-heading mb-3 scroll-mt-6">
            AIに何をさせ、何をさせないのか
          </h2>
          <p className="text-text-sub leading-relaxed mb-6">
            otomochi が使うAIは、いずれも otomochi 自身が保持するモデルを、otomochi
            の管理下で動かしています。
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-lg font-bold text-text-heading mb-3">させること</h3>
              <ul className="space-y-3">
                {aiDoes.map((item) => (
                  <li key={item.title}>
                    <Card className="flex items-start gap-3">
                      <Check
                        className="w-5 h-5 text-success flex-shrink-0 mt-0.5"
                        aria-hidden="true"
                      />
                      <div>
                        <p className="text-sm font-bold text-text-heading mb-1">{item.title}</p>
                        <p className="text-sm text-text leading-relaxed">{item.body}</p>
                      </div>
                    </Card>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h3 className="text-lg font-bold text-text-heading mb-3">させないこと</h3>
              <ul className="space-y-3">
                {aiDoesNot.map((item) => (
                  <li key={item.title}>
                    <Card className="flex items-start gap-3">
                      <X className="w-5 h-5 text-danger flex-shrink-0 mt-0.5" aria-hidden="true" />
                      <div>
                        <p className="text-sm font-bold text-text-heading mb-1">{item.title}</p>
                        <p className="text-sm text-text leading-relaxed">{item.body}</p>
                      </div>
                    </Card>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <p className="text-sm text-text-sub leading-relaxed mt-6">
            なお、AIが出力したテキストの正確性・完全性・特定の目的への適合性を otomochi
            が保証するものではありません。内容をご確認のうえ、必要に応じて編集してお使いください。
          </p>
        </section>

        {/* 3. 外部提供先 */}
        <section aria-labelledby="third-party" className="mb-16">
          <h2 id="third-party" className="text-2xl font-bold text-text-heading mb-3 scroll-mt-6">
            外部に情報が渡る先
          </h2>
          <p className="text-text-sub leading-relaxed mb-6">
            otomochi は、原則としてあなたの個人情報を第三者に提供しません。サービスを動かすために情報が渡るのは、次の先だけです。
          </p>

          <ul className="space-y-4 mb-6">
            {thirdParties.map((p) => (
              <li key={p.name}>
                <Card>
                  <h3 className="text-base font-bold text-text-heading mb-2">{p.name}</h3>
                  <p className="text-sm text-text mb-2">
                    <span className="text-text-sub">渡る情報：</span>
                    {p.data}
                  </p>
                  <p className="text-sm text-text leading-relaxed">{p.note}</p>
                </Card>
              </li>
            ))}
          </ul>

          <Card className="space-y-3">
            <p className="text-sm text-text leading-relaxed">
              上記のほか、法令にもとづく場合や、人の生命・身体・財産の保護のために必要な場合など、プライバシーポリシーが定める例外にあたる場合に限り、個人情報を提供することがあります。
            </p>
            <p className="text-sm text-text leading-relaxed">
              あなたが送信した音声データその他の情報は、法令にもとづく場合を除いて、第三者に開示・提供しません。
            </p>
            <p className="text-sm text-text-sub leading-relaxed">
              くわしくは
              <Link
                to="/privacy"
                className="mx-1 text-primary hover:text-primary-hover underline underline-offset-2"
              >
                プライバシーポリシー
              </Link>
              の第2条（個人情報等の範囲）・第4条（第三者への提供）・{PP_ARTICLE_SECRECY}
              をご覧ください。
            </p>
          </Card>
        </section>

        {/* 4. 時間・回数の買い足し（出典: 利用規約 第4条5項・6項ただし書、特商法表記「返品の取扱条件」「不良品の取扱条件」）。
            金額は本体3文書に無いため書かない（terms-simple-facts.md ギャップ⑧） */}
        <section aria-labelledby="purchase" className="mb-16">
          <h2 id="purchase" className="text-2xl font-bold text-text-heading mb-3 scroll-mt-6">
            時間・回数を買い足したとき
          </h2>
          <Card className="space-y-3">
            <p className="text-sm text-text leading-relaxed">
              必要な分だけ、利用時間や書き起こし回数を買い足せます（都度課金）。買い足しはその都度のお支払いで、自動で更新・課金されることはありません。
            </p>
            <p className="text-sm text-text leading-relaxed">
              買い足した時間・回数は、月ごとの利用枠とは別に扱われます。課金期間の区切りをまたいでも、使い切るまで残ります。
            </p>
            <p className="text-sm text-text leading-relaxed">
              購入手続きが完了したあとのキャンセル・返金には応じられません。ただし、買い足した時間・回数が追加されなかった場合は、お問い合わせいただければ返金します。また、otomochi 側の重大な不具合が確認された場合は、状況を確認のうえ、必要に応じて個別に対応します。
            </p>
          </Card>
          <p className="text-sm text-text-sub leading-relaxed mt-4">
            金額や買い足せるプランは
            <Link
              to="/pricing"
              className="mx-1 text-primary hover:text-primary-hover underline underline-offset-2"
            >
              料金プランのページ
            </Link>
            でご確認いただけます。
          </p>
        </section>

        {/* 5. 退会・解約 */}
        <section aria-labelledby="leaving" className="mb-16">
          <h2 id="leaving" className="text-2xl font-bold text-text-heading mb-3 scroll-mt-6">
            アカウントを消したいとき・解約したいとき
          </h2>

          <h3 className="text-lg font-bold text-text-heading mt-6 mb-3">
            有料プランをやめたいとき（解約）
          </h3>
          <Card className="space-y-3">
            <p className="text-sm text-text leading-relaxed">
              <strong>手順：</strong>マイページから、いつでも解約手続きができます。
            </p>
            <p className="text-sm text-text leading-relaxed">
              <strong>そのとき起きること：</strong>
              解約の手続きをしたあとも、すでに課金済みの利用期間の満了日（次回更新日の前日）までは、そのままご利用いただけます。すでにお支払いいただいた利用料金の返金は行いません。月の途中で解約した場合も、日割り計算による返金は行いません。
            </p>
            <p className="text-sm text-text leading-relaxed">
              このサービスは通信販売にあたるため、特定商取引法にもとづくクーリング・オフ制度の対象外です。
            </p>
          </Card>

          <h3 className="text-lg font-bold text-text-heading mt-8 mb-3">
            アカウントごと消したいとき（退会）
          </h3>
          <Card className="space-y-3">
            <p className="text-sm text-text leading-relaxed">
              <strong>手順：</strong>
              マイページから、いつでも退会・アカウント削除の申請ができます。削除の実行前に確認画面が表示され、あなたの明確な操作があってはじめて申請を受け付けます。
            </p>
            <p className="text-sm text-text leading-relaxed">
              <strong>そのとき起きること：</strong>
              アカウント情報（メールアドレス、ニックネーム、認証情報）、連携ログインの情報、利用履歴、プラン情報などを削除します。保持期限が来ていない音声データや書き起こし結果が残っている場合は、それらも削除の対象に含まれます。決済代行サービスに保存されている顧客情報・サブスクリプション情報についても、削除の手続きを行います。
            </p>
            <p className="text-sm text-text leading-relaxed">
              これらの削除は、<strong>退会申請を受け付けてから30日以内に完了</strong>します。
            </p>
            <p className="text-sm text-text leading-relaxed">
              有料プランの契約中に退会を申請した場合、その時点で有料プランは解約されます。この場合も、すでにお支払いいただいた利用料金の返金は行いません。
              <strong>
                有料プランを解約して、利用期間が終わってから退会する方法をおすすめします
              </strong>
              が、すぐに退会することもできます。
            </p>
            <p className="text-sm text-text-sub leading-relaxed">
              なお、電子帳簿保存法・所得税法などの法令で保存が義務づけられている取引記録については、法令が定める保存期間が過ぎるまで保持する場合があります。
            </p>
          </Card>

          <p className="text-sm text-text-sub leading-relaxed mt-4">
            退会しなくても、otomochi
            が持っているあなたの個人情報について、開示・訂正・追加・削除・利用停止・消去を求めることができます。ご本人であることを確認したうえで、遅滞なく対応します。
          </p>
        </section>

        {/* 6. 連絡先 */}
        <section aria-labelledby="contact" className="mb-16">
          <h2 id="contact" className="text-2xl font-bold text-text-heading mb-3 scroll-mt-6">
            困ったときの連絡先
          </h2>
          <Card className="space-y-4">
            <div className="flex items-start gap-3">
              <Mail className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" aria-hidden="true" />
              <div>
                <p className="text-sm font-bold text-text-heading mb-1">メールでのお問い合わせ</p>
                <p className="text-sm text-text">
                  <a
                    href="mailto:support@hoshikusaya.com"
                    className="text-primary hover:text-primary-hover underline underline-offset-2"
                  >
                    support@hoshikusaya.com
                  </a>
                </p>
                <p className="text-sm text-text-sub mt-1 leading-relaxed">
                  個人情報の取扱いに関するご質問・苦情も、こちらの窓口で受け付けています。
                </p>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <Link to="/contact" className={linkButtonClass('primary', '', 'sm')}>
                お問い合わせフォーム
              </Link>
              <Link to="/faq" className={linkButtonClass('secondary', '', 'sm')}>
                よくある質問
              </Link>
            </div>
          </Card>
        </section>

        {/* 監修表記。監修の対象が本体3文書であることが読み取れる文脈に置く。 */}
        <div className="border-t border-text-sub/20 pt-8 space-y-3">
          <h2 className="text-base font-bold text-text-heading">
            利用規約・プライバシーポリシー・特定商取引法に基づく表記について
          </h2>
          <p className="text-sm text-text leading-relaxed">
            otomochi の利用規約・プライバシーポリシー・特定商取引法に基づく表記は、行政書士の監修を受けています。
          </p>
          <p className="text-sm text-text">{SUPERVISOR}</p>
          <p className="text-xs text-text-sub leading-relaxed">
            ※上記の監修は、利用規約・プライバシーポリシー及び特定商取引法に基づく表記に関する法的観点からの確認・助言であり、サービスの内容・機能を保証するものではありません。
            <br />
            ※本ページは上記文書の要点を平易に説明した補助資料であり、法的効力を持つものではありません。
          </p>
          <p className="text-xs text-text-sub">最終更新日：{LAST_UPDATED}</p>
          <p className="text-sm">
            <Link
              to="/terms"
              className="inline-flex items-center gap-1 text-primary hover:text-primary-hover underline underline-offset-2"
            >
              利用規約の全文を読む
              <ArrowRight className="w-3.5 h-3.5" aria-hidden="true" />
            </Link>
          </p>
        </div>
      </main>
    </Layout>
  );
}
